import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Hourglass, Check, X } from "lucide-react";

const KEY = "velluto_trial_dismissed";

// Gentle countdown while a member is inside their 7-day free trial, with a one-tap "keep it".
export function TrialBanner() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [hidden, setHidden] = useState(() => sessionStorage.getItem(KEY) === "1");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!user || user.role === "admin") return;
    api.get("/billing/subscription").then(({ data }) => setSub(data)).catch(() => {});
  }, [user]);
  if (!user || hidden || !sub?.has_subscription || sub.status !== "trialing" || sub.keep_confirmed) return null;
  const days = sub.trial_days_left ?? 0;
  const ends = sub.trial_end ? new Date(sub.trial_end).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
  const keep = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/keep");
      toast.success(data.cancel_at_period_end ? "Saved" : "Wonderful — your membership continues after the trial");
      setSub((s) => ({ ...s, keep_confirmed: true, cancel_at_period_end: data.cancel_at_period_end }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update"); } finally { setBusy(false); }
  };
  const dismiss = () => { sessionStorage.setItem(KEY, "1"); setHidden(true); };
  return (
    <div data-testid="trial-banner" className="mb-8 rounded-2xl border border-gold/40 bg-gold/8 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
      <span className="w-10 h-10 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0"><Hourglass size={16} className="text-gold" /></span>
      <div className="flex-1 min-w-0">
        <p data-testid="trial-days" className="text-sm">
          {days <= 0 ? "Your free trial ends today" : `${days} day${days === 1 ? "" : "s"} left in your free trial`}
          {sub.cancel_at_period_end ? <span className="text-amber-300"> · set to end {ends}</span> : ends ? <span className="text-text-muted"> · continues {ends}</span> : null}
        </p>
        <p className="text-xs text-text-muted mt-0.5">
          {sub.cancel_at_period_end ? "Tap keep it and nothing changes when the trial ends." : "Keep every door open — one tap and you're set."}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <button data-testid="trial-keep-btn" onClick={keep} disabled={busy}
          className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gold text-ink text-[10px] uppercase tracking-[0.15em] hover:bg-gold/90 transition-colors disabled:opacity-50">
          <Check size={12} /> {busy ? "…" : "Keep it"}
        </button>
        <Link to="/premium" data-testid="trial-manage" className="px-4 py-2.5 rounded-full border border-white/15 text-text-muted text-[10px] uppercase tracking-[0.15em] hover:text-text-main transition-colors">Manage</Link>
        <button data-testid="trial-dismiss" onClick={dismiss} aria-label="Dismiss" className="p-1.5 text-text-muted hover:text-text-main"><X size={14} /></button>
      </div>
    </div>
  );
}
